import { useEffect, useMemo, useCallback, useState } from "react"
import NaverMapView from "@/components/views/NaverMapView"
import {
  fetchCampingSites,
  selectCampingSites,
  selectCampingSitesStatus,
  updateGPSByUser,
  fetchGPSForMarkerOnly,
  selectPendingSites,
  fetchPendingCampingSites,
} from "@/features/maps/mapsSlice"
import { useAppDispatch, useAppSelector } from "@/app/hooks"
import { selectCurrentUser } from "@/features/auth/authSlice"
import { IoLocateOutline } from "react-icons/io5"
import { MapMarkersProvider, useMapMarkers } from "@/contexts/MapMarkersContext"
import type { LocationData } from "@/components/MapMarkerLayer"
import { MapMarkerLayer } from "@/components/MapMarkerLayer"
import TopSearchBar from "@/components/TopSearchBar"
import { useNavigate, useLocation } from "react-router-dom"
import { TagFilter } from "@/components/TagFilter"
import RegisterPlaceButton from "@/components/common/RegisterPlaceButton"
import type { TagResponse } from "@/types/domain"
import api from "@/services/api" // Import api
import { Button } from "@/components/ui"
import "./MapPage.css"
import { setMapCenter, setGPSFetchedByUser } from "@/features/maps/mapsSlice"

const MapPageContent = () => {
  const dispatch = useAppDispatch()
  const currentUser = useAppSelector(selectCurrentUser)
  const isAdmin = currentUser?.role === "ADMIN"

  const campingSites = useAppSelector(selectCampingSites)
  const pendingSites = useAppSelector(selectPendingSites)
  const status = useAppSelector(selectCampingSitesStatus)
  const { setMapInstance } = useMapMarkers()
  const navigate = useNavigate()
  const location = useLocation()

  const [tagGroups, setTagGroups] = useState<
    Record<string, { title: string; tags: TagResponse[] }>
  >({})
  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  const [selectedTags, setSelectedTags] = useState<number[]>([])

  // query parameter에서 siteId 가져오기
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const siteIdFromQuery = queryParams.get("siteId")

  // localStorage에서 마지막 위치 가져오기
  const [initialCenter] = useState<{ lat: number; lng: number } | undefined>(
    () => {
      const savedLocation = localStorage.getItem("lastMapLocation")
      if (savedLocation) {
        const { lat, lng } = JSON.parse(savedLocation)
        return { lat, lng }
      }
      return undefined
    },
  )

  // localStorage에서 마지막 줌 레벨 가져오기
  const [initialZoom] = useState<number | undefined>(() => {
    const savedZoom = localStorage.getItem("lastMapZoom")
    if (savedZoom) {
      return parseInt(savedZoom, 10)
    }
    return undefined
  })

  useEffect(() => {
    if (siteIdFromQuery && campingSites.length > 0) {
      const targetSite = campingSites.find(site => site.id === siteIdFromQuery)
      if (targetSite) {
        dispatch(setMapCenter({ lat: targetSite.latitude, lng: targetSite.longitude }))
        dispatch(setGPSFetchedByUser(true))
        
        // 상세 페이지 열기
        navigate(`/sites/${targetSite.id}`, { replace: true })
      }
    }
  }, [siteIdFromQuery, campingSites, dispatch, navigate])

  const handleGetCurrentLocation = () => {
    dispatch(updateGPSByUser())
  }

  useEffect(() => {
    // GPS 권한이 있으면 현재 위치 마커 표시 (지도 이동 없음)
    dispatch(fetchGPSForMarkerOnly())
  }, [dispatch])

  // Fetch tags logic moved from TagFilter.tsx
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await api.get<Record<string, TagResponse[]>>("/api/tags")
        const data = response.data

        if (typeof data === "object" && data !== null) {
          const groups: Record<string, { title: string; tags: TagResponse[] }> =
            {}
          for (const groupName in data) {
            if (Object.prototype.hasOwnProperty.call(data, groupName)) {
              const apiTagsInGroup = data[groupName]
              if (apiTagsInGroup.length > 0) {
                const tagGroupTitle = apiTagsInGroup[0].tagGroupTitle
                groups[groupName] = {
                  title: tagGroupTitle,
                  tags: apiTagsInGroup,
                }
              }
            }
          }
          setTagGroups(groups)
        } else {
          console.error("Tag data is not in the expected format.")
        }
      } catch (error) {
        console.error("태그 정보를 가져오는데 실패했습니다.", error)
      }
    }
    void fetchTags()
  }, []) // Empty dependency array means it runs once on mount

  useEffect(() => {
    // 선택된 태그가 변경될 때마다 캠핑장 정보 다시 요청
    void dispatch(
      fetchCampingSites({
        request: { page: 0, size: 100, tagIds: selectedTags },
        isAdmin: false, // 기존 로직 유지
      }),
    )

    // 어드민인 경우 미승인 캠핑장도 요청
    if (isAdmin) {
      void dispatch(
        fetchPendingCampingSites({
          page: 0,
          size: 100,
          tagIds: selectedTags,
        }),
      )
    }
  }, [selectedTags, dispatch, isAdmin])

  const handleGroupClick = useCallback((groupName: string) => {
    setActiveGroup(prevGroup => (prevGroup === groupName ? null : groupName))
  }, [])

  const handleTagClick = useCallback((tagId: number) => {
    setSelectedTags(prevSelected => {
      if (prevSelected.includes(tagId)) {
        return prevSelected.filter(id => id !== tagId)
      } else {
        return [...prevSelected, tagId]
      }
    })
  }, [])

  const handleSaveLocation = useCallback((lat: number, lng: number) => {
    localStorage.setItem("lastMapLocation", JSON.stringify({ lat, lng }))
  }, [])

  const handleSaveZoom = useCallback((zoom: number) => {
    localStorage.setItem("lastMapZoom", zoom.toString())
  }, [])

  const locations = useMemo<LocationData[]>(() => {
    // API의 siteType을 프론트엔드 타입으로 매핑
    const mapSiteType = (apiType: string | undefined): LocationData["type"] => {
      if (!apiType) return "캠핑장"

      // API에서 받은 타입을 한글 타입으로 변환
      const typeMap: Record<string, LocationData["type"]> = {
        노지: "노지",
        주차장: "주차장",
        캠핑장: "캠핑장",
        설비: "설비",
        기타: "기타",
      }

      return typeMap[apiType] || "캠핑장"
    }

    const approvedLocations: LocationData[] = campingSites
      .filter(site => site.id)
      .map(site => ({
        id: site.id,
        name: site.name,
        latitude: site.latitude,
        longitude: site.longitude,
        type: mapSiteType(site.siteType),
        icon: site.icon,
        imageUrl: site.thumbUrl,
        isPending: false,
      }))

    const pendingLocations: LocationData[] = isAdmin
      ? pendingSites
          .filter(site => site.id)
          .map(site => ({
            id: site.id,
            name: site.name,
            latitude: site.latitude,
            longitude: site.longitude,
            type: mapSiteType(site.businessType), // CampingSiteResponse uses businessType
            icon: undefined, // CampingSiteResponse doesn't have icon directly here
            imageUrl: site.representativeImageUrl, // CampingSiteResponse uses representativeImageUrl
            isPending: true,
          }))
      : []

    console.log("MapPage Computed Locations:", {
      approvedCount: approvedLocations.length,
      pendingCount: pendingLocations.length,
      isAdmin,
    })

    return [...approvedLocations, ...pendingLocations]
  }, [campingSites, pendingSites, isAdmin])

  const handleMarkerClick = useCallback(
    (location: LocationData) => {
      console.log("위치 클릭:", location)
      navigate(`/sites/${location.id}`)
    },
    [navigate],
  )

  return (
    <div className="map-page-container">
      <TopSearchBar />
      <div className="filter-and-register-container">
        <TagFilter
          tagGroups={tagGroups}
          activeGroup={activeGroup}
          onGroupClick={handleGroupClick}
        />
      </div>
      {activeGroup && (
        <div className="sub-tags-container">
          {tagGroups[activeGroup].tags.map(tag => (
            <button
              key={tag.id}
              className={`tag-button ${
                selectedTags.includes(tag.id) ? "active" : ""
              }`}
              onClick={() => { handleTagClick(tag.id); }}
            >
              {tag.name}
            </button>
          ))}
        </div>
      )}
      <div className="map-view-wrapper">
        {status === "loading" && (
          <div className="overlay-message">Loading...</div>
        )}
        {status === "failed" && (
          <div className="overlay-message">Error fetching data</div>
        )}
        <NaverMapView
          initialCenter={initialCenter}
          initialZoom={initialZoom}
          onMapLoad={setMapInstance}
          onCenterChanged={handleSaveLocation}
          onZoomChanged={handleSaveZoom}
        />
        <MapMarkerLayer
          locations={locations}
          onMarkerClick={handleMarkerClick}
        />
        <RegisterPlaceButton className="floating-register-button" />
        <Button
          variant="icon"
          onClick={handleGetCurrentLocation}
          className="current-location-btn"
          aria-label="현재 위치 찾기"
          title="현재 위치 찾기"
        >
          <IoLocateOutline size={24} />
        </Button>
      </div>
    </div>
  )
}

export const MapPage = () => {
  return (
    <MapMarkersProvider>
      <MapPageContent />
    </MapMarkersProvider>
  )
}
